import classNames from 'classnames';
import React, { memo, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  COLOR,
  fieldDisabledOpacity,
  fieldLabelFontSize,
  fieldLabelMargin,
  FONT_SIZE,
  SIZE,
} from '../../styles';
import { FieldInfo } from '../FieldInfo';
import Radio from './Radio';
import { type RadioGroupProps, type RadioValueType } from './Radio.types';

const RadioGroup: React.FC<RadioGroupProps> = memo((props) => {
  const {
    checkedValue,
    refFunc,
    onBlur,
    children,
    className = '',
    colorMode = 'light',
    dataTestId,
    disabled,
    errorText,
    hintText,
    isInline = true,
    name,
    padded,
    title,
  } = props;

  const textFieldRef = useRef(null);
  const [activeValue, setActiveValue] = useState(checkedValue);
  const [focus, setFocus] = useState(false);
  const [hover, setHover] = useState(false);

  const addFocus = () => setFocus(true);
  const removeFocus = () => setFocus(false);
  const addHover = () => setHover(true);
  const removeHover = () => setHover(false);

  const setupReference = useCallback(
    (node: HTMLElement) => {
      // eslint-disable-next-line
      // @ts-ignore
      textFieldRef.current = node;
      if (refFunc) refFunc(node);
    },
    [refFunc],
  );

  // FIXME: index will be wrong here.
  // eslint-disable-next-line
  // @ts-ignore
  const toggleRadio = () => handleOnChange({ value: activeValue });

  const handleOnChange = ({ index = 0, value }: { index?: number; value: RadioValueType }) => {
    if (value === checkedValue) {
      return;
    }
    props.onValueChange?.(value);

    if ('changeByProp' in props) {
      // eslint-disable-next-line
      // @ts-ignore
      props.onChange?.(props.changeByProp, value);
    } else {
      // eslint-disable-next-line
      // @ts-ignore
      props.onChange?.({ index, value });
    }
  };

  // eslint-disable-next-line
  // @ts-ignore
  const onFocus = ({ value }) => {
    addFocus();
    setActiveValue(value);
  };

  const active = focus || hover;

  const fieldsetClass = classNames(
    className,
    'radio-group',
    `mode-${colorMode}`,
    { active },
    { disabled },
    { error: errorText },
    { padded },
  );

  const radioProps = {
    active,
    checkedValue,
    ...(disabled ? { disabled } : {}),
    focus,
    handleOnChange,
    name,
    onFocus,
    onBlur,
    setupReference,
    toggleRadio,
  };

  const updatedChildren = children
    // eslint-disable-next-line
    // @ts-ignore
    .filter((child) => !!child)
    // eslint-disable-next-line
    // @ts-ignore
    .map((child, index) => {
      // If there are raw strings in the list of children, trying to `cloneElement()`
      // them will throw an exception.
      if (typeof child === 'string') {
        return child;
      }

      // TODO: check if the child is a React.Fragment,
      //       if it is, expand the child and clone each children of this child
      const isChildRadio = child?.type === Radio;

      return React.cloneElement(child, {
        index,
        key: `${name}-${index + 1}`,
        ...child.props,
        ...(isChildRadio
          ? { ...radioProps, dataTestId: child.props.dataTestId ?? dataTestId }
          : {}),
      });
    });
  return (
    <Wrapper
      className={fieldsetClass}
      onBlur={removeFocus}
      onFocus={addFocus}
      onMouseOver={addHover}
      onMouseOut={removeHover}
    >
      {title && <h3 className="group-title">{title}</h3>}
      {isInline && <section className="inline-options">{updatedChildren}</section>}
      {!isInline && hintText && <FieldInfo text={hintText} />}
      {!isInline && updatedChildren}
      {!errorText && hintText && isInline && <FieldInfo text={hintText} />}
      {errorText && <FieldInfo type="error" text={errorText} />}
    </Wrapper>
  );
});

// Styled Components
export const Wrapper = styled.fieldset`
  border: none;
  font-family: inherit;
  margin: 0 0 ${SIZE['20']};
  padding: 0;

  fieldset + &:last-child {
    margin-bottom: 0;
  }

  &:focus,
  label:focus {
    outline: none;
  }

  &.disabled {
    &,
    input:disabled,
    .group-title,
    label {
      cursor: not-allowed;
    }

    .group-title {
      opacity: ${fieldDisabledOpacity};
    }

    .radiomark-wrapper input {
      & ~ .radiomark,
      &:checked ~ .radiomark {
        background-color: ${COLOR['light-gray']};
        border-color: ${COLOR['light-gray']};
      }
    }
  }

  &.padded {
    margin: 0 0 ${SIZE['16']} 0;
  }

  &.error {
    label {
      color: ${COLOR['red-dark']};
    }

    .radiomark-wrapper input {
      & ~ .radiomark,
      &:checked ~ .radiomark {
        background-color: ${COLOR['red-dark']};
        border-color: ${COLOR['red-dark']};
      }
    }
  }

  &.active:not(.disabled) label:hover,
  &.active:not(.disabled) label:focus,
  label.active:not(.disabled) {
    color: ${COLOR['primary']};

    .radiomark-wrapper input {
      & ~ .radiomark,
      &:checked ~ .radiomark {
        background-color: ${COLOR['primary']};
        border-color: ${COLOR['primary']};
      }
    }
  }

  &.mode-dark {
    .group-title,
    label {
      color: ${COLOR['white']};
    }
  }

  label.disabled {
    &,
    &:hover,
    &:focus {
      color: ${COLOR['fieldLabelColor']};
    }
  }

  &,
  label,
  input,
  .group-title {
    box-sizing: border-box;
  }

  &,
  label,
  .group-title {
    display: block;
  }

  label,
  .group-title {
    color: ${COLOR['fieldLabelColor']};
    cursor: pointer;
    font-size: calc(${fieldLabelFontSize} + 1px);
    margin: ${fieldLabelMargin};
    padding: 0;

    &.disabled {
      cursor: not-allowed;
      opacity: ${fieldDisabledOpacity};
      pointer-events: none;
    }
  }

  label {
    .field-legend {
      margin-left: 24px;
    }
  }

  .group-title {
    font-weight: 400;
    margin-bottom: ${SIZE['8']};

    & + .field-legend {
      margin-top: -3px;
      margin-bottom: 5px;
    }
  }

  .inline-options {
    display: flex;

    label {
      display: inline-block;
      margin-left: ${SIZE['28']};

      &:first-child {
        margin-left: 0;
      }
    }
  }

  .radiomark-wrapper {
    -moz-user-select: none;
    -ms-user-select: none;
    -webkit-user-select: none;
    cursor: pointer;
    display: inline-block;
    font-size: ${FONT_SIZE['22']};
    height: ${SIZE['16']};
    margin: 0 ${SIZE['8']} 0 0;
    position: relative;
    user-select: none;
    vertical-align: text-bottom;
    width: ${SIZE['16']};

    &.checked .radiomark,
    input:checked ~ .radiomark {
      background-color: ${COLOR['primary']};
      border-color: ${COLOR['primary']};

      &:after {
        display: block;
      }
    }

    input {
      cursor: pointer;
      height: 0;
      opacity: 0;
      position: absolute;
      width: 0;
    }

    .radiomark {
      border: 1px solid ${COLOR['fieldLabelColor']};
      border-radius: 50%;
      box-sizing: border-box;
      height: ${SIZE['16']};
      left: 0;
      position: absolute;
      top: 0;
      width: ${SIZE['16']};

      &:after {
        background: ${COLOR['white']};
        border-radius: 50%;
        content: '';
        display: none;
        height: ${SIZE['8']};
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: ${SIZE['8']};
      }
    }
  }

  &.mode-dark {
    &,
    &.active:not(.disabled) {
      label,
      .group-title {
        color: ${COLOR['white']};
      }

      label.disabled {
        &,
        &:hover,
        &:focus {
          color: ${COLOR['white']};
        }
      }

      .radiomark {
        border-color: ${COLOR['white']};
      }

      .radiomark-wrapper input {
        & ~ .radiomark,
        &:checked ~ .radiomark {
          background-color: ${COLOR['dark-gray-lighter']};
          border-color: ${COLOR['dark-gray-lighter']};
        }
      }

      label:hover,
      label:focus {
        color: ${COLOR['primary-light']};

        .field-legend {
          color: ${COLOR['primary-light']};
        }
      }

      label.active:not(.disabled) {
        color: ${COLOR['white']};

        .field-legend {
          color: ${COLOR['white']};
        }

        .radiomark-wrapper input {
          & ~ .radiomark,
          &:checked ~ .radiomark {
            background-color: ${COLOR['primary-light']};
            border-color: ${COLOR['primary-light']};
          }
        }
      }
    }
  }
`;

// Display Name
RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
